import { useRef } from 'react'
import { cn } from 'src/shared/components/ui/utils'
import { DecorationCard } from '../../tabs/OverviewTab/DecorationCard'
import type { GalleryDesignRow } from '../../../utils/quoteItems'
import { DRAG_THRESHOLD } from '../consts'
import type { CardPosition } from '../types'

type DragState = {
  pointerId: number
  startClientX: number
  startClientY: number
  originX: number
  originY: number
  moved: boolean
}

export const CanvasCard = ({
  item,
  position,
  scale,
  zIndex,
  selected,
  publicId,
  isReadOnly,
  hideMockups = false,
  onSelect,
  onMove,
  onMoveEnd,
  onClick,
  onEditClick,
  onDuplicate,
  onDelete,
}: {
  item: GalleryDesignRow
  position: CardPosition
  scale: number
  zIndex: number
  selected: boolean
  publicId?: string
  isReadOnly?: boolean
  hideMockups?: boolean
  onSelect: (opts?: { additive?: boolean }) => void
  onMove: (position: CardPosition) => void
  onMoveEnd?: () => void
  onClick?: () => void
  onEditClick?: () => void
  onDuplicate?: () => void
  onDelete?: () => void
}) => {
  const dragState = useRef<DragState | null>(null)

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    // Let modal dismiss win; don't open product preview from the same gesture.
    if (document.querySelector('[role="dialog"][data-state="open"]')) return
    e.stopPropagation()
    onSelect({ additive: e.shiftKey || e.metaKey || e.ctrlKey })
    // Let real action buttons (edit / generate / gallery) receive their click.
    if ((e.target as HTMLElement).closest('button')) return
    dragState.current = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      originX: position.x,
      originY: position.y,
      moved: false,
    }
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      // Pointer may already be released; drag still works via bubbling events.
    }
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragState.current
    if (!drag || drag.pointerId !== e.pointerId) return
    const dxClient = e.clientX - drag.startClientX
    const dyClient = e.clientY - drag.startClientY
    if (!drag.moved && Math.hypot(dxClient, dyClient) > DRAG_THRESHOLD) {
      drag.moved = true
    }
    if (isReadOnly) return
    if (drag.moved) {
      onMove({
        x: drag.originX + dxClient / scale,
        y: drag.originY + dyClient / scale,
      })
    }
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragState.current
    if (!drag || drag.pointerId !== e.pointerId) return
    dragState.current = null
    onMoveEnd?.()
    if (!drag.moved) onClick?.()
  }

  return (
    <div
      className={cn(
        'absolute left-0 top-0 select-none transition-shadow',
        isReadOnly ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing',
        selected
          ? 'rounded-xl shadow-xl ring-2 ring-blue-500 ring-offset-2'
          : 'hover:shadow-xl'
      )}
      style={{
        transform: `translate(${position.x}px, ${position.y}px)`,
        zIndex,
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => {
        dragState.current = null
        onMoveEnd?.()
      }}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >
      <DecorationCard
        design={item.design}
        productName={item.productName}
        decorationName={item.decorationName}
        quoteItemId={item.quoteItemId}
        quoteSubItemId={item.quoteSubItemId}
        price={item.price}
        fallbackImage={item.fallbackImage ?? undefined}
        mockup_urls={hideMockups ? undefined : item.mockup_urls ?? undefined}
        public_id={publicId}
        isReadOnly={isReadOnly}
        compact
        onEditClick={onEditClick}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        variantColors={item.productPreview?.product_variant_colors}
        chosenVariantColorId={
          item.productPreview?.chosen_product_variant_color?.id
        }
      />
    </div>
  )
}
